/**
 * remark plugin: converts `<br>` / `<br/>` occurrences in markdown into
 * mdast `break` nodes (which ReactMarkdown then renders as a real `<br>`
 * element).
 *
 * Two source shapes are handled, because remarkGfm parses `<br>` into
 * different mdast nodes depending on context:
 *
 *   1. Inside a GFM table cell, `<br>` is emitted as its own `html` node
 *      (sibling to surrounding text nodes), because cells are parsed as
 *      a constrained inline subset that promotes inline HTML to standalone
 *      nodes. This is the primary case for the table builder's multi-line
 *      cells.
 *   2. Elsewhere in flowing prose, `<br>` survives inside the value of a
 *      `text` node verbatim — markdown phrasing doesn't auto-split text
 *      around inline HTML.
 *
 * Both branches replace the matched HTML with mdast `break` nodes.
 *
 * Why a controlled plugin rather than just enabling `rehype-raw`:
 * `rehype-raw` lets arbitrary inline HTML through, which is XSS-equivalent
 * since admins can author articles. The delta-chip plugin established the
 * pattern of recognizing specific syntax in text and emitting controlled
 * mdast nodes — this is the same pattern applied to `<br>`.
 *
 * Primary use case: multi-line cells in the table builder. GFM tables
 * disallow literal newlines inside cells, so the builder serializes
 * each in-cell line break as `<br>` and the parser converts it back to
 * `\n` for display in the dialog's multiline `TextField`.
 */

import type { Node, Parent } from "unist";

interface TextNode extends Node {
  type: "text";
  value: string;
}

interface HtmlNode extends Node {
  type: "html";
  value: string;
}

// `<br>`, `<br/>`, `<br />`, any case. No attributes — those would be
// suspicious and we'd rather emit them as escaped literal text.
const BR_PATTERN = /<br\s*\/?>/gi;
// Strict version: the ENTIRE node value is just a `<br>` tag (with
// optional surrounding whitespace). Used for `html` nodes, where the
// value is the standalone tag rather than a chunk of prose.
const BR_ONLY_PATTERN = /^\s*<br\s*\/?>\s*$/i;

const remarkLineBreak = () => {
  return (tree: Node) => {
    const transform = (
      node: Node,
      parent: Parent | null,
      index: number,
    ): number | undefined => {
      // Depth-first: process children first so children of replaced
      // nodes don't get touched twice. Mirrors `remarkDeltaChip`.
      const asParent = node as Parent;
      if (Array.isArray(asParent.children)) {
        let i = 0;
        while (i < asParent.children.length) {
          const consumed = transform(asParent.children[i], asParent, i);
          i += consumed ?? 1;
        }
      }

      if (!parent) return;

      // Standalone `<br>` html node (the GFM table cell case): swap the
      // whole node for a single `break`. Anything in an html node that
      // ISN'T just `<br>` falls through untouched — ReactMarkdown will
      // escape it, which is exactly the XSS posture we want.
      if (node.type === "html") {
        const value = (node as HtmlNode).value;
        if (!BR_ONLY_PATTERN.test(value)) return;
        parent.children.splice(index, 1, { type: "break" } as Node);
        return 1;
      }

      if (node.type !== "text") return;
      const text = (node as TextNode).value;
      BR_PATTERN.lastIndex = 0;
      const matches: RegExpMatchArray[] = [];
      let m: RegExpExecArray | null;
      while ((m = BR_PATTERN.exec(text)) !== null) {
        matches.push(m);
      }
      if (matches.length === 0) return;

      const newNodes: Node[] = [];
      let lastEnd = 0;
      for (const match of matches) {
        const start = match.index ?? 0;
        if (start > lastEnd) {
          newNodes.push({
            type: "text",
            value: text.slice(lastEnd, start),
          } as TextNode);
        }
        // mdast `break` renders as `<br>` natively — no `data.hName`
        // needed since `break` is a standard mdast node type.
        newNodes.push({ type: "break" } as Node);
        lastEnd = start + match[0].length;
      }
      if (lastEnd < text.length) {
        newNodes.push({
          type: "text",
          value: text.slice(lastEnd),
        } as TextNode);
      }

      parent.children.splice(index, 1, ...newNodes);
      return newNodes.length;
    };

    transform(tree, null, 0);
  };
};

export default remarkLineBreak;
